import { CaseKeySchema } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useCaseCandidates } from '../../hooks/useIssueTodo.js';
import { formatIssueTime } from '../../lib/issue-mgmt-format.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 案件を紐づける小窓。Backlog の新しいメンズ案件（未対応・作成日の新しい順）から1つ選ぶか、案件のキーを貼って決定する。
 * ミラーに無いキーも確認待ちで紐づく（次の同期で状態が入る）。決定したらトーストで知らせ、元に戻せるようにする。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 開発が要る課題に、Backlog の未対応のメンズ案件を選ぶかキーを貼って紐づける小窓
 * @graph-connects useCaseCandidates [calls] 紐づけの候補
 * @graph-connects useIssueMgmtWrite [calls] linkCase
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function LinkCaseDialog({
  issue,
  onClose,
  onLinked,
  now,
}: {
  issue: { id: string; title: string };
  onClose: () => void;
  /** 紐づけた後に呼ぶ（やることのカードを済みにするため）。operationId は元に戻すときに使う */
  onLinked?: (caseKey: string, operationId: string | null) => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const candidates = useCaseCandidates(true);
  const linkCase = useIssueMgmtWrite('linkCase');
  const [picked, setPicked] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const pastedKey = pasted.trim() === '' ? null : CaseKeySchema.safeParse(pasted);
  const caseKey = pastedKey === null ? picked : pastedKey.success ? pastedKey.data : null;
  const hint =
    pastedKey !== null && !pastedKey.success
      ? t('issueMgmt.linkCase.invalidKey')
      : caseKey === null
        ? t('issueMgmt.linkCase.hint')
        : '';

  const submit = () => {
    if (caseKey === null) return;
    linkCase.mutate(
      { issueId: issue.id, caseKey },
      {
        onSuccess: (result) => {
          onClose();
          onLinked?.(caseKey, result.operationId);
          toast.notifySaved(t('issueMgmt.linkCase.saved', { title: issue.title, key: caseKey }), {
            operationId: result.operationId,
            detail: t('issueMgmt.linkCase.savedDetail'),
          });
        },
        onError: toast.notifyError,
      },
    );
  };

  const list = candidates.data?.candidates ?? [];
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t('issueMgmt.linkCase.title', { title: issue.title })}
      description={issue.id}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">{hint}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button disabled={caseKey === null} loading={linkCase.isPending} onClick={submit}>
              {t('issueMgmt.common.decide')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-sm font-semibold text-[var(--color-text-primary)]">
            {t('issueMgmt.linkCase.candidates')}
          </p>
          {candidates.isError ? (
            <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>
          ) : candidates.data === undefined ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('issueMgmt.common.loading')}
            </p>
          ) : list.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('issueMgmt.linkCase.noCandidates')}
            </p>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {list.map((candidate) => {
                const selected = pastedKey === null && picked === candidate.caseKey;
                const created =
                  candidate.createdAt === null
                    ? null
                    : formatIssueTime(candidate.createdAt, null, now);
                return (
                  <button
                    key={candidate.caseKey}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      setPicked(candidate.caseKey);
                      setPasted('');
                    }}
                    className={`block w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      selected
                        ? 'border-[var(--color-accent-primary)] bg-[color-mix(in_srgb,var(--color-accent-primary)_8%,transparent)]'
                        : 'border-[var(--color-border)] hover:border-[var(--color-border-hover)]'
                    }`}
                  >
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      {candidate.caseKey}
                    </span>
                    <span className="ml-2 text-[var(--color-text-primary)]">
                      {candidate.title ?? t('issueMgmt.common.dash')}
                    </span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      {candidate.statusName ?? t('issueMgmt.common.dash')}
                      {created !== null &&
                        `・${t('issueMgmt.linkCase.createdAt', { date: created })}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label
            htmlFor="issue-link-case-key"
            className="mb-1 block text-sm font-semibold text-[var(--color-text-primary)]"
          >
            {t('issueMgmt.linkCase.pasteLabel')}
          </label>
          <Input
            id="issue-link-case-key"
            placeholder="AIRCLOSET-123456"
            value={pasted}
            onChange={(e) => {
              setPasted(e.target.value);
            }}
            fullWidth
          />
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('issueMgmt.linkCase.mirrorNote')}
          </p>
        </div>
      </div>
    </Modal>
  );
}
